import { useEffect, useMemo, useState } from "react";
import type { TelegramStatus } from "@baton/contracts";
import { ApiError, ApiErrorCodes, type DashboardApi } from "./api.js";
import { CopyField } from "./components/command.js";
import { Modal } from "./components/modal.js";
import { qrSvgPath } from "./qr.js";

export const TELEGRAM_CONSENT =
  "The Baton server decrypts messages to and from your agents only while relaying them to Telegram. Nothing is stored. Telegram chats are not end-to-end encrypted.";

const STATUS_POLL_MS = 3_000;
const UNAVAILABLE = "Telegram is not enabled on this Baton server";

interface MintedLink { url: string; expiresAt: string }

/**
 * Links this member's Telegram chat to the relay. The deep link is minted only after the
 * consent click, lives in this component's state, is drawn as a QR in the page, and is
 * forgotten as soon as the status poll reports the chat linked or the dialog closes.
 */
export function TelegramDialog(props: { open: boolean; onClose(): void; api: DashboardApi }) {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [link, setLink] = useState<MintedLink | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!props.open) { setStatus(null); setStatusError(null); setLink(null); setError(null); setWorking(false); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await props.api.telegramStatus();
        if (cancelled) return;
        setStatus(next); setStatusError(null); setNow(Date.now());
        if (next.linked) setLink(null);
      } catch (caught) {
        if (!cancelled) setStatusError(caught instanceof Error ? caught.message : "Could not read the Telegram status");
      }
    };
    void poll();
    const timer = setInterval(() => { void poll(); setNow(Date.now()); }, STATUS_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [props.open, props.api]);

  async function mint() {
    setWorking(true); setError(null);
    try {
      const created = await props.api.createTelegramLink({ acknowledged: true, idempotencyKey: crypto.randomUUID() });
      setLink({ url: created.url, expiresAt: created.expiresAt });
      setNow(Date.now());
    } catch (caught) {
      setError(caught instanceof ApiError && caught.is(ApiErrorCodes.telegramUnavailable)
        ? UNAVAILABLE
        : caught instanceof Error ? caught.message : "Could not create the Telegram link");
    } finally {
      setWorking(false);
    }
  }

  async function unlink() {
    setWorking(true); setError(null);
    try {
      await props.api.unlinkTelegram();
      setStatus(await props.api.telegramStatus());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not unlink Telegram");
    } finally {
      setWorking(false);
    }
  }

  const expired = link !== null && Date.parse(link.expiresAt) <= now;

  return <Modal open={props.open} onClose={props.onClose} title="Telegram">
    <div className="telegram-dialog" data-testid="telegram-dialog">
      <p className="muted">Get messages to your agents on your phone, answer as your general agent, and set a listening message for agents that are offline.</p>
      <aside className="telegram-consent" data-testid="telegram-consent">{TELEGRAM_CONSENT}</aside>
      {status === null && !statusError && <p className="muted compact">Checking this server…</p>}
      {statusError && <p className="error">{statusError}</p>}
      {status && !status.available && <p className="telegram-unavailable" data-testid="telegram-unavailable">{UNAVAILABLE}</p>}
      {status?.available && status.linked && <div className="telegram-linked" data-testid="telegram-linked">
        <span className="presence-dot online" aria-hidden="true" />
        <span>Linked {status.linkedAt ? relativeTime(status.linkedAt, now) : ""}{status.botUsername ? ` to @${status.botUsername}` : ""}</span>
        <span aria-hidden="true">·</span>
        <button type="button" className="link danger" disabled={working} onClick={() => void unlink()}>Unlink</button>
      </div>}
      {status?.available && !status.linked && <>
        {status.botUsername && <p className="muted compact">Bot: @{status.botUsername}. Scanning the code opens it; tapping Start links this chat. The code works once and expires after ten minutes.</p>}
        {!link && <button type="button" className="primary" disabled={working} onClick={() => void mint()}>{working ? "Preparing…" : "Show QR"}</button>}
        {link && <div className="telegram-link">
          <QrCode value={link.url} />
          <div className="telegram-link-detail">
            <CopyField label="Telegram link" value={link.url} />
            <p className="muted compact">{expired
              ? "This code expired."
              : `Expires ${new Date(link.expiresAt).toLocaleTimeString([], { timeStyle: "short" })} · waiting for the scan…`}</p>
            <button type="button" className="secondary" disabled={working} onClick={() => void mint()}>{expired ? "Show a new QR" : "New QR"}</button>
          </div>
        </div>}
      </>}
      {error && <p className="error">{error}</p>}
    </div>
  </Modal>;
}

/** Inline SVG drawn from the URL in memory: no image request, no data URL, no attribute carrying the code. */
function QrCode({ value }: { value: string }) {
  const { size, path } = useMemo(() => qrSvgPath(value), [value]);
  return <svg className="telegram-qr" data-testid="telegram-qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="QR code that opens the bot in Telegram" shapeRendering="crispEdges">
    <rect width={size} height={size} fill="#ffffff" />
    <path d={path} fill="#0f172a" />
  </svg>;
}

/** "now", "3 minutes ago", …; a link time is never in the future, so clock skew is clamped. */
function relativeTime(iso: string, now: number): string {
  const seconds = Math.min(0, Math.round((Date.parse(iso) - now) / 1_000));
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(seconds) < 60) return format.format(seconds, "second");
  if (Math.abs(seconds) < 3_600) return format.format(Math.round(seconds / 60), "minute");
  if (Math.abs(seconds) < 86_400) return format.format(Math.round(seconds / 3_600), "hour");
  return format.format(Math.round(seconds / 86_400), "day");
}
